import { getLocalizedPath, getLangFromPath, stripLanguageFromPath, withLanguagePath, pageSeo } from "@/lib/i18nRoutes";
import { getNavLabel } from "@/config/navigation";
import { isNavPathActive, mobileNavigationTabs } from "@/lib/navigationAI";

const STACK_STORAGE_KEY = "marstick_mobile_tab_stacks_v1";

const APP_NATIVE_ROOT_PATHS = ["/dashboard", "/weather", "/navigation", "/trips", "/boats"];
const APP_NATIVE_TITLE_KEYS = {
  dashboard: "dashboard",
  weather: "weather",
  navigation: "navigation",
  trips: "trips",
  boats: "boats",
};

function getNativeRootForPath(pathname = window.location.pathname) {
  const normalized = stripLanguageFromPath(pathname).replace(/\/+$/, "") || "/";
  return APP_NATIVE_ROOT_PATHS.find((root) => normalized === root || normalized.startsWith(`${root}/`)) || null;
}

export function getMobileTabRoot(group, lang = "en") {
  const firstItem = group?.items?.[0];
  if (!firstItem) return `/${lang}`;
  return firstItem.zone === "public" ? withLanguagePath(firstItem.path, lang) : firstItem.path;
}

export function getMobileTabPath(item, lang = "en") {
  if (!item) return `/${lang}`;
  return item.zone === "public" ? withLanguagePath(item.path, lang) : item.path;
}

export function getActiveMobileTab(pathname = window.location.pathname, lang = getLangFromPath(pathname)) {
  const tabs = mobileNavigationTabs.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.path !== "/radar"),
  }));

  return tabs.find((group) => group.items.some((item) => {
    const target = getMobileTabPath(item, lang);
    return isNavPathActive(pathname, target) || isNavPathActive(pathname, item.path);
  })) || tabs[0];
}

export function getMobileRootPaths(lang = "en") {
  return mobileNavigationTabs
    .map((group) => ({ ...group, items: group.items.filter((item) => item.path !== "/radar") }))
    .filter((group) => group.items.length > 0)
    .map((group) => getMobileTabRoot(group, lang));
}

export function isMobileRootScreen(pathname = window.location.pathname, lang = getLangFromPath(pathname)) {
  const normalized = stripLanguageFromPath(pathname).replace(/\/+$/, "") || "/";
  if (normalized === "/") return true;
  if (APP_NATIVE_ROOT_PATHS.includes(normalized)) return true;
  return getMobileRootPaths(lang).some((root) => {
    const rootNormalized = (stripLanguageFromPath(root).replace(/\/+$/, "") || "/");
    return normalized === rootNormalized;
  });
}

export function getMobileNavigationStackDepth(pathname = window.location.pathname, lang = getLangFromPath(pathname)) {
  if (isMobileRootScreen(pathname, lang)) return 0;

  const normalized = (stripLanguageFromPath(pathname).replace(/\/+$/, "") || "/");
  const nativeRoot = getNativeRootForPath(pathname);
  if (nativeRoot && normalized.startsWith(`${nativeRoot}/`)) {
    return normalized.slice(nativeRoot.length).split("/").filter(Boolean).length;
  }

  const activeGroup = getActiveMobileTab(pathname, lang);
  const root = (stripLanguageFromPath(getMobileTabRoot(activeGroup, lang)).replace(/\/+$/, "") || "/");

  if (root !== "/" && normalized.startsWith(`${root}/`)) {
    return normalized.slice(root.length).split("/").filter(Boolean).length;
  }

  return normalized.split("/").filter(Boolean).length;
}

export function getMobilePageTitle(pathname = window.location.pathname, lang = getLangFromPath(pathname)) {
  const normalized = stripLanguageFromPath(pathname).replace(/\/+$/, "") || "/";
  if (normalized === "/") return "Marstick";
  const nativeRoot = getNativeRootForPath(pathname);
  if (nativeRoot) {
    const key = nativeRoot.slice(1);
    return getNavLabel(APP_NATIVE_TITLE_KEYS[key], lang) || "Marstick";
  }
  const activeTab = getActiveMobileTab(pathname, lang);
  const matchedItem = activeTab?.items?.find((item) => {
    const target = getMobileTabPath(item, lang);
    return isNavPathActive(pathname, target) || isNavPathActive(pathname, item.path);
  });
  const seoKey = matchedItem?.key;
  const seoTitle = seoKey ? pageSeo[seoKey]?.[lang]?.title : null;
  const label = seoKey ? getNavLabel(seoKey, lang) : "";
  return (seoTitle || label || "Marstick").split("|")[0].trim();
}

export function readMobileStacks() {
  try {
    return JSON.parse(sessionStorage.getItem(STACK_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

export function writeMobileStack(groupLabel, pathname) {
  if (!groupLabel || !pathname) return;
  const stacks = readMobileStacks();
  stacks[groupLabel] = pathname;
  sessionStorage.setItem(STACK_STORAGE_KEY, JSON.stringify(stacks));
}

export function getMobileStackPath(groupLabel) {
  return readMobileStacks()[groupLabel] || null;
}